import { useUserRegion } from "@/hooks/useUserRegion";
import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  orderBy,
  onSnapshot,
  setDoc,
  deleteDoc
} from "firebase/firestore";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { useBrokerDocuments, BrokerDocument } from "@/hooks/useBrokerDocuments";
import { useBrokerMessages } from "@/hooks/useBrokerMessages";
import { BrokerSidebar } from "@/components/layout/BrokerSidebar";
import { DashboardHeader } from "@/components/layout/DashboardHeader";
import {
  FileText,
  Users,
  Building2,
  DollarSign,
  TrendingUp,
  Eye,
  Calendar,
  UserPlus,
  BarChart3,
  Plus,
  Link as LinkIcon,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  Shield,
  Video,
  FolderOpen,
  Upload,
  Download,
  Trash2,
  Star,
  Briefcase,
  Loader2,
  Send,
  Pencil,
  Copy,
  Mail,
  ClipboardList,
  CheckCircle2,
  FilePen
} from "lucide-react";
import { PartnerAssignmentsView } from "@/components/partner-assignments";
import { SubmitDealForm } from "@/components/submit-deal/SubmitDealForm";
import { downloadDealFormPdf } from "@/utils/deal-form-pdf";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ConsultantsTab, PartnersTab, ExpertsTab, CalendarTab } from "@/components/dashboard/shared";
import { CompactCalendarWidget } from "@/components/dashboard/CompactCalendarWidget";
import { OnboardingGuide } from '@/components/onboarding/OnboardingGuide';
import { SellerContactRequests } from "@/components/dashboard/ContactRequestsTracker";
import { LISTING_DETAILS } from "@/data/listing-details.data";
import SecureDocumentsSection from "@/components/seller/SecureDocumentsSection";
import UpdateImpactDataDialog from "@/components/seller/UpdateImpactDataDialog";
import CIMApprovalBanner from "@/components/seller/CIMApprovalBanner";

// Translations
const translations = {
  en: {
    title: "Broker Dashboard",
    subtitle: "Manage your listings and represented sellers",
    addSeller: "Add Seller",
    listNewBusiness: "List New Business",
    totalListings: "Total Listings",
    active: "Active",
    underNegotiation: "Under Negotiation",
    closedDeals: "Closed Deals",
    totalViews: "Total Views",
    loiReceived: "LOI Received",
    commissionEarned: "Commission Earned",
    commissionPending: "Commission Pending",
    avgDaysOnMarket: "Avg. Days on Market",
    days: "days",
    overview: "Overview",
    myListings: "My Listings",
    representedSellers: "Represented Sellers",
    messages: "Messages",
    documents: "Documents",
    partners: "Partners",
    ourExperts: "Our Experts",
    topPerformingListings: "Top Performing Listings",
    basedOnViewsAndInquiries: "Based on views and inquiries",
    noListingsYet: "No listings yet",
    views: "Views",
    inquiries: "Inquiries",
    lois: "LOIs",
    viewDetails: "View Details",
    recentActivity: "Recent Activity",
    latestUpdates: "Latest updates across all listings",
    lastActivity: "Last activity",
    sellersYouRepresent: "Sellers you currently represent",
    noRepresentedSellers: "No represented sellers yet",
    addYourFirstSeller: "Add Your First Seller",
    portalAccess: "Portal Access:",
    activeStatus: "Active",
    agreement: "Agreement:",
    signed: "Signed",
    activeDeals: "Active Deals:",
    allBusinesses: "All businesses you've listed on behalf of sellers",
    newListing: "New Listing",
    createFirstListing: "Create Your First Listing",
    listing: "listing",
    listings: "listings",
    viewMergedCalendar: "View Merged Calendar",
    askingPrice: "Asking Price",
    commission: "Commission",
    daysOnMarket: "Days on Market",
    status: "Status",
    performanceMetrics: "Performance Metrics",
    ndas: "NDAs",
    meetings: "Meetings",
    sellerHasFullAccess: "Seller Has Full Access",
    canViewActivity: "can view all activity and respond to inquiries",
    viewActivityLog: "View Activity Log",
    manageListing: "Manage Listing",
    viewPublicPage: "View Public Page",
    listingsWithoutSeller: "Listings Without Assigned Seller",
    pleaseAssignSeller: "Please assign a seller to this listing",
    selectSeller: "Select Seller",
    newSeller: "New Seller",
    managePortalAccess: "Manage sellers you represent and their portal access",
    portalActive: "Portal Active",
    portalPending: "Portal Pending",
    agreementSigned: "Agreement Signed",
    awaitingSig: "Awaiting Signature",
    contact: "Contact",
    totalValue: "Total Value",
    added: "Added",
    agreementDetails: "Agreement Details",
    signedDate: "Signed:",
    expiresDate: "Expires:",
    viewAgreement: "View Agreement",
    viewFullProfile: "View Full Profile",
    message: "Message",
    loadingDashboard: "Loading dashboard...",
    errorLoading: "Error",
    failedToLoad: "Failed to load dashboard data",
    sellerAdded: "Seller Added",
    sellerAddedDesc: "Seller has been added and will receive portal access invitation",
    failedToAddSeller: "Failed to add seller",
    sellerLinked: "Seller Linked",
    sellerLinkedDesc: "has been linked to this deal and granted access",
    failedToLinkSeller: "Failed to link seller to deal",
    addRepresentedSeller: "Add Represented Seller",
    addSellerDescription: "Add a seller you represent. They will receive portal access to track their listings.",
    fullName: "Full Name",
    company: "Company",
    email: "Email",
    phone: "Phone",
    whatHappensNext: "What happens next:",
    step1: "Seller receives an invitation email with portal access",
    step2: "A merged calendar is created combining your availability and theirs",
    step3: "Seller can view all their listings and respond to inquiries",
    step4: "Both you and the seller receive notifications for all activities",
    cancel: "Cancel",
    // Messages
    noMessages: "No messages yet",
    startConversation: "Start a conversation",
    typeMessage: "Type your message...",
    send: "Send",
    // Documents
    noDocuments: "No documents yet",
    uploadDocument: "Upload Document",
    downloadDocument: "Download",
    deleteDocument: "Delete",
    contracts: "Contracts",
    agreements: "Agreements",
    financials: "Financials",
    other: "Other",
    // Partners & Experts
    expertAssignments: "Expert Assignments",
    partnerNetwork: "Partner Network",
    internalExperts: "Internal Experts",
    externalConsultants: "External Consultants",
    comingSoon: "Coming soon...",
    pendingApprovalTitle: "Deals Awaiting Seller Approval",
    // AJOUTÉ — actions carte vendeur représenté
    sendInvite: "Send Invitation",
    resendInvite: "Resend Invitation",
    sendingInvite: "Sending...",
    inviteSent: "Invitation sent",
    inviteSentDesc: "The invitation email was sent successfully.",
    inviteFailed: "Invitation failed",
    inviteFailedDesc: "Could not send the invitation email. You can copy the link below and share it manually.",
    inviteFailedBadge: "Last invitation attempt failed",
    copyLink: "Copy Link",
    linkCopied: "Link copied",
    linkCopiedDesc: "The invitation link has been copied to your clipboard.",
    editSeller: "Edit",
    editSellerTitle: "Edit Represented Seller",
    editSellerDesc: "Update this seller's information.",
    saveChanges: "Save Changes",
    sellerUpdated: "Seller updated",
    sellerUpdatedDesc: "The seller's information has been updated.",
    deleteSeller: "Delete",
    deleteSellerTitle: "Delete this represented seller?",
    deleteSellerDescBlocked: "This seller has {count} deal(s) attached and cannot be deleted. Please contact staff if you need to remove this seller.",
    deleteSellerDescConfirm: "This will remove {name} from your represented sellers list. This action cannot be undone.",
    deleteSellerConfirm: "Delete Seller",
    sellerDeleted: "Seller removed",
    sellerDeletedDesc: "The seller has been removed from your list.",
    // AJOUTÉ — bouton Formulaire (dealFormDrafts)
    dealForm: "Deal Form",
    shareForm: "Share Blank Form",
    reshareForm: "Re-share Blank Form",
    shareFormHint: "Seller fills it in themselves",
    formShared: "Form link copied",
    formSharedDesc: "The form link has been copied to your clipboard. Share it with the seller.",
    formStatusPending: "Sent — not yet opened",
    formStatusInProgress: "In progress",
    formStatusSubmitted: "Approved by seller — ready to complete",
    openSellerForm: "Open Seller's Form",
    formSubmittedBadge: "Form ready to review",
    // AJOUTÉ — brouillon interne du broker (distinct du "Deal Form" ci-dessus)
    myDraft: "My Draft",
    draftInProgress: "Draft in progress",
    resumeDraft: "Resume Draft",
    lastSaved: "Last saved",
  },
  fr: {
    title: "Tableau de bord Courtier",
    subtitle: "Gérez vos annonces et vendeurs représentés",
    addSeller: "Ajouter un vendeur",
    listNewBusiness: "Nouvelle annonce",
    totalListings: "Total des annonces",
    active: "Actives",
    underNegotiation: "En négociation",
    closedDeals: "Transactions conclues",
    totalViews: "Vues totales",
    loiReceived: "LOI reçues",
    commissionEarned: "Commission gagnée",
    commissionPending: "Commission en attente",
    avgDaysOnMarket: "Jours moyens sur le marché",
    days: "jours",
    overview: "Aperçu",
    myListings: "Mes annonces",
    representedSellers: "Vendeurs représentés",
    messages: "Messages",
    documents: "Documents",
    partners: "Partenaires",
    ourExperts: "Nos experts",
    topPerformingListings: "Meilleures annonces",
    basedOnViewsAndInquiries: "Basé sur les vues et demandes",
    noListingsYet: "Aucune annonce pour le moment",
    views: "Vues",
    inquiries: "Demandes",
    lois: "LOIs",
    viewDetails: "Voir les détails",
    recentActivity: "Activité récente",
    latestUpdates: "Dernières mises à jour de toutes les annonces",
    lastActivity: "Dernière activité",
    sellersYouRepresent: "Vendeurs que vous représentez actuellement",
    noRepresentedSellers: "Aucun vendeur représenté pour le moment",
    addYourFirstSeller: "Ajouter votre premier vendeur",
    portalAccess: "Accès portail :",
    activeStatus: "Actif",
    agreement: "Contrat :",
    signed: "Signé",
    activeDeals: "Transactions actives :",
    allBusinesses: "Toutes les entreprises listées pour le compte des vendeurs",
    newListing: "Nouvelle annonce",
    createFirstListing: "Créer votre première annonce",
    listing: "annonce",
    listings: "annonces",
    viewMergedCalendar: "Voir le calendrier fusionné",
    askingPrice: "Prix demandé",
    commission: "Commission",
    daysOnMarket: "Jours sur le marché",
    status: "Statut",
    performanceMetrics: "Métriques de performance",
    ndas: "NDAs",
    meetings: "Réunions",
    sellerHasFullAccess: "Le vendeur a un accès complet",
    canViewActivity: "peut voir toute l'activité et répondre aux demandes",
    viewActivityLog: "Voir le journal d'activité",
    manageListing: "Gérer l'annonce",
    viewPublicPage: "Voir la page publique",
    listingsWithoutSeller: "Annonces sans vendeur assigné",
    pleaseAssignSeller: "Veuillez assigner un vendeur à cette annonce",
    selectSeller: "Sélectionner un vendeur",
    newSeller: "Nouveau vendeur",
    managePortalAccess: "Gérez les vendeurs que vous représentez et leur accès au portail",
    portalActive: "Portail actif",
    portalPending: "Portail en attente",
    agreementSigned: "Contrat signé",
    awaitingSig: "En attente de signature",
    contact: "Contact",
    totalValue: "Valeur totale",
    added: "Ajouté",
    agreementDetails: "Détails du contrat",
    signedDate: "Signé le :",
    expiresDate: "Expire le :",
    viewAgreement: "Voir le contrat",
    viewFullProfile: "Voir le profil complet",
    message: "Message",
    loadingDashboard: "Chargement du tableau de bord...",
    errorLoading: "Erreur",
    failedToLoad: "Échec du chargement des données",
    sellerAdded: "Vendeur ajouté",
    sellerAddedDesc: "Le vendeur a été ajouté et recevra une invitation d'accès au portail",
    failedToAddSeller: "Échec de l'ajout du vendeur",
    sellerLinked: "Vendeur lié",
    sellerLinkedDesc: "a été lié à cette transaction et a obtenu l'accès",
    failedToLinkSeller: "Échec de la liaison du vendeur à la transaction",
    addRepresentedSeller: "Ajouter un vendeur représenté",
    addSellerDescription: "Ajoutez un vendeur que vous représentez. Il recevra un accès portail pour suivre ses annonces.",
    fullName: "Nom complet",
    company: "Entreprise",
    email: "Courriel",
    phone: "Téléphone",
    whatHappensNext: "Prochaines étapes :",
    step1: "Le vendeur reçoit un courriel d'invitation avec accès au portail",
    step2: "Un calendrier fusionné est créé combinant vos disponibilités",
    step3: "Le vendeur peut voir toutes ses annonces et répondre aux demandes",
    step4: "Vous et le vendeur recevez des notifications pour toutes les activités",
    cancel: "Annuler",
    // Messages
    noMessages: "Aucun message pour le moment",
    startConversation: "Démarrer une conversation",
    typeMessage: "Tapez votre message...",
    send: "Envoyer",
    // Documents
    noDocuments: "Aucun document pour le moment",
    uploadDocument: "Téléverser un document",
    downloadDocument: "Télécharger",
    deleteDocument: "Supprimer",
    contracts: "Contrats",
    agreements: "Ententes",
    financials: "Documents financiers",
    other: "Autres",
    // Partners & Experts
    expertAssignments: "Affectations d'experts",
    partnerNetwork: "Réseau de partenaires",
    internalExperts: "Experts internes",
    externalConsultants: "Consultants externes",
    comingSoon: "Bientôt disponible...",
    pendingApprovalTitle: "Deals en attente d'approbation vendeur",
    // AJOUTÉ — actions carte vendeur représenté
    sendInvite: "Envoyer l'invitation",
    resendInvite: "Renvoyer l'invitation",
    sendingInvite: "Envoi...",
    inviteSent: "Invitation envoyée",
    inviteSentDesc: "Le courriel d'invitation a été envoyé avec succès.",
    inviteFailed: "Échec de l'invitation",
    inviteFailedDesc: "Impossible d'envoyer le courriel d'invitation. Vous pouvez copier le lien ci-dessous et le partager manuellement.",
    inviteFailedBadge: "Dernière tentative d'invitation échouée",
    copyLink: "Copier le lien",
    linkCopied: "Lien copié",
    linkCopiedDesc: "Le lien d'invitation a été copié dans le presse-papiers.",
    editSeller: "Modifier",
    editSellerTitle: "Modifier le vendeur représenté",
    editSellerDesc: "Mettez à jour les informations de ce vendeur.",
    saveChanges: "Enregistrer",
    sellerUpdated: "Vendeur mis à jour",
    sellerUpdatedDesc: "Les informations du vendeur ont été mises à jour.",
    deleteSeller: "Supprimer",
    deleteSellerTitle: "Supprimer ce vendeur représenté ?",
    deleteSellerDescBlocked: "Ce vendeur a {count} transaction(s) rattachée(s) et ne peut pas être supprimé. Contactez le personnel si vous devez retirer ce vendeur.",
    deleteSellerDescConfirm: "Ceci retirera {name} de votre liste de vendeurs représentés. Cette action est irréversible.",
    deleteSellerConfirm: "Supprimer le vendeur",
    sellerDeleted: "Vendeur retiré",
    sellerDeletedDesc: "Le vendeur a été retiré de votre liste.",
    // AJOUTÉ — bouton Formulaire (dealFormDrafts)
    dealForm: "Formulaire de deal",
    shareForm: "Partager formulaire vierge",
    reshareForm: "Repartager formulaire vierge",
    shareFormHint: "Le vendeur le remplit lui-même",
    formShared: "Lien du formulaire copié",
    formSharedDesc: "Le lien du formulaire a été copié dans le presse-papiers. Partagez-le avec le vendeur.",
    formStatusPending: "Envoyé — pas encore ouvert",
    formStatusInProgress: "En cours",
    formStatusSubmitted: "Approuvé par le vendeur — prêt à compléter",
    openSellerForm: "Ouvrir le formulaire du vendeur",
    formSubmittedBadge: "Formulaire prêt à réviser",
    // AJOUTÉ — brouillon interne du broker (distinct du "Formulaire de deal" ci-dessus)
    myDraft: "Mon brouillon",
    draftInProgress: "Brouillon en cours",
    resumeDraft: "Reprendre le brouillon",
    lastSaved: "Dernière sauvegarde",
  }
};

// Types
interface Deal {
  id: string;
  businessName: string;
  businessDescription?: string;
  askingPrice: number;
  currency: string;
  status: string;
  createdAt: any;
}

interface BrokerDeal extends Deal {
  brokerId: string;
  brokerCommission: number;
  commissionType: 'percentage' | 'fixed';
  representedSeller?: RepresentedSeller;
  dealStatus: 'listed' | 'under_negotiation' | 'closed' | 'cancelled';
  kpis: DealKPIs;
}

interface RepresentedSeller {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  representationAgreement: {
    signed: boolean;
    documentUrl?: string;
    signedDate?: any;
    expiryDate?: any;
  };
  calendarId: string;
  isActive: boolean;
  addedAt: any;
  hasPortalAccess: boolean;
  // AJOUTÉ — suivi de l'état d'envoi de l'invitation
  inviteSentAt?: any;
  inviteStatus?: 'sent' | 'failed';
  inviteError?: string;
  // AJOUTÉ — suivi du formulaire de deal partagé (dealFormDrafts).
  // formToken référence le document dealFormDrafts/{formToken} ; un
  // nouveau clic sur "Formulaire" réutilise le même token tant qu'il
  // n'est pas encore soumis (re-partage = même lien), en génère un
  // nouveau seulement après soumission (pour repartir sur un formulaire
  // vierge si un second deal doit être monté pour ce même vendeur).
  formToken?: string;
  formStatus?: 'pending' | 'in_progress' | 'submitted';
  formSharedAt?: any;
  formSubmittedAt?: any;
  // CONSERVÉS UNIQUEMENT POUR MIGRATION — ces champs singuliers ne
  // représentent plus l'état réel une fois la sous-collection dealForms
  // en place (un vendeur peut avoir plusieurs formulaires/compagnies en
  // parallèle). Lus une seule fois au chargement pour créer les entrées
  // dealForms correspondantes si elles n'existent pas encore (voir
  // migrateLegacyDealForms ci-dessous), puis ignorés par l'UI.
  brokerDraftToken?: string;
  brokerDraftStatus?: 'in_progress' | null;
  brokerDraftUpdatedAt?: any;
  brokerDraftBusinessName?: string;
}

// AJOUTÉ — une entrée = un formulaire/deal en cours pour un vendeur
// représenté donné (un vendeur peut en avoir plusieurs, une par
// compagnie). Le contenu réel (formData) reste dans dealFormDrafts/{token}
// — cette entrée n'est qu'un index léger + un état dénormalisé pour
// l'affichage, lu au chargement (pas temps réel).
interface DealFormEntry {
  id: string; // = token
  token: string;
  origin: 'broker' | 'shared';
  createdAt?: any;
  updatedAt?: any;
  copiedFromFormId?: string;
  openedAsFormId?: string;
  finalizedDealId?: string;
  finalizedAt?: any;
  liveStatus?: 'pending' | 'in_progress' | 'submitted';
  liveBusinessName?: string;
}

interface DealKPIs {
  views: number;
  inquiries: number;
  ndaRequests: number;
  ndaSigned: number;
  loiReceived: number;
  meetingsScheduled: number;
  averageResponseTime: number;
  daysOnMarket: number;
  lastActivity?: any;
}

// Deal dealSubmissions en attente d'approbation vendeur — affiché en
// lecture seule via CIMApprovalBanner (voir loadBrokerDashboard()).
interface PendingApprovalDeal {
  id: string;
  businessName?: string;
  legalName?: string;
}

export default function BrokerDashboard() {
  const { user } = useAuth();
  const userRegion = useUserRegion(user?.id);

  const { language } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;

  // Hooks for documents and messages
  const { documents, loading: docsLoading, uploadDocument, deleteDocument, getDocumentsByType } = useBrokerDocuments();
  const { 
    conversations, 
    messages, 
    activeConversation, 
    setActiveConversation, 
    loading: msgsLoading, 
    sending, 
    sendMessage,
    createConversation 
  } = useBrokerMessages();

  // State
  const [activeTab, setActiveTab] = useState("overview");
  const [myListings, setMyListings] = useState<BrokerDeal[]>([]);
  const [representedSellers, setRepresentedSellers] = useState<RepresentedSeller[]>([]);
  const [pendingApprovalDeals, setPendingApprovalDeals] = useState<PendingApprovalDeal[]>([]);
  const [totalKPIs, setTotalKPIs] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const [showAddSeller, setShowAddSeller] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<string | null>(null);
  const [messageText, setMessageText] = useState("");
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadType, setUploadType] = useState<BrokerDocument['type']>('contract');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [favoriteConsultants, setFavoriteConsultants] = useState<string[]>([]);

  // AJOUTÉ — état des actions sur la carte vendeur représenté
  const [invitingSellerId, setInvitingSellerId] = useState<string | null>(null);
  const [editingSeller, setEditingSeller] = useState<RepresentedSeller | null>(null);
  const [deletingSeller, setDeletingSeller] = useState<RepresentedSeller | null>(null);
  const [deletingSellerDealCount, setDeletingSellerDealCount] = useState<number>(0);
  const [checkingDeleteEligibility, setCheckingDeleteEligibility] = useState(false);

  // AJOUTÉ — vendeur dont on reprend le brouillon interne broker (voir
  // SubmitDealForm.tsx brokerMode/resumeSellerId). null = "Nouvelle
  // Annonce" démarre toujours un formulaire vierge, peu importe l'état
  // React ou Firestore laissé par le dernier deal travaillé.
  const [resumeSellerId, setResumeSellerId] = useState<string | null>(null);
  // AJOUTÉ — identifie précisément QUELLE entrée dealForms reprendre,
  // maintenant qu'un vendeur peut en avoir plusieurs en parallèle.
  const [resumeFormId, setResumeFormId] = useState<string | null>(null);
  // AJOUTÉ — liste des formulaires/deals en cours par vendeur (clé =
  // sellerId), chargée/rafraîchie par l'effet ci-dessous.
  const [dealFormsBySeller, setDealFormsBySeller] = useState<Record<string, DealFormEntry[]>>({});
  const [dealFormsRefreshKey, setDealFormsRefreshKey] = useState(0);
  const refreshDealForms = () => setDealFormsRefreshKey(k => k + 1);

  // Load favorite consultants from Firestore
  useEffect(() => {
    if (!user?.id) return;
    const favCol = collection(db, "users", user.id, "consultantsFavorites");
    const unsub = onSnapshot(favCol, (snap) => {
      const ids: string[] = [];
      snap.forEach((d) => ids.push(d.id));
      setFavoriteConsultants(ids);
    });
    return () => unsub();
  }, [user?.id]);

  // Toggle consultant favorite with real-time notification
  const toggleConsultantFavorite = async (consultantId: string, consultantName?: string) => {
    if (!user?.id) return;
    const favRef = doc(db, "users", user.id, "consultantsFavorites", consultantId);
    const isFavorite = favoriteConsultants.includes(consultantId);
    
    if (isFavorite) {
      await deleteDoc(favRef);
      toast({
        title: language === 'fr' ? "Retiré des favoris" : "Removed from favorites",
        description: consultantName || consultantId,
      });
    } else {
      await setDoc(favRef, { addedAt: serverTimestamp() });
      toast({
        title: language === 'fr' ? "Ajouté aux favoris ❤️" : "Added to favorites ❤️",
        description: consultantName || consultantId,
      });
    }
  };

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    await uploadDocument(file, uploadType);
    setShowUploadDialog(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle send message
  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    const success = await sendMessage(messageText);
    if (success) {
      setMessageText("");
    }
  };

  // Handle start conversation with seller
  const handleStartConversation = async (seller: RepresentedSeller) => {
    // Check if conversation exists
    const existingConv = conversations.find(c => c.participantId === seller.id);
    if (existingConv) {
      setActiveConversation(existingConv.id);
    } else {
      await createConversation(seller.id, seller.name, 'seller');
    }
    setActiveTab('messages');
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

    useEffect(() => {
    if (user) {
      loadBrokerDashboard();
    }
  }, [user]);

    // AJOUTÉ — listener temps réel sur representedSellers : le badge de
  // statut du formulaire (formStatus: pending/in_progress/submitted) se
  // met à jour automatiquement dès que le vendeur soumet, sans que le
  // broker ait besoin de recharger la page. Séparé de
  // loadBrokerDashboard() (qui reste un chargement one-shot pour le
  // reste — deals, KPIs) car onSnapshot maintient sa propre
  // souscription active, à désabonner proprement au démontage.
  // Ce même listener reflète aussi brokerDraftToken/brokerDraftStatus,
  // donc la section "Mon brouillon" se met à jour automatiquement dès
  // que saveDraft() écrit dans SubmitDealForm.tsx.
  useEffect(() => {
    if (!user?.id) return;
    const sellersQuery = query(
      collection(db, "representedSellers"),
      where("brokerId", "==", user.id),
      where("isActive", "==", true)
    );
    const unsubscribe = onSnapshot(sellersQuery, (snapshot) => {
      const sellersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as RepresentedSeller[];
      setRepresentedSellers(sellersData);
    }, (error) => {
      console.error("Error in representedSellers listener:", error);
    });
    return () => unsubscribe();
  }, [user?.id]);

  // AJOUTÉ — charge la liste des formulaires/deals par vendeur (sous-
  // collection dealForms) + leur état vivant (status/nom d'entreprise,
  // lus depuis dealFormDrafts/{token}). Se relance quand representedSellers
  // change (nouveau vendeur, etc.) OU quand refreshDealForms() est appelé
  // après une action (partage, ouverture, sauvegarde) — la sous-collection
  // elle-même n'est pas suivie en temps réel, ce chargement est un
  // instantané rafraîchi à la demande, cohérent avec le reste de ce
  // dashboard (pendingApprovalDeals, myListings, etc.).
  //
  // Migration : si un vendeur a encore d'anciens champs singuliers
  // (brokerDraftToken / formToken, d'avant l'introduction de cette
  // sous-collection), une entrée dealForms équivalente est créée si elle
  // n'existe pas déjà (idempotent — l'ID du document = le token, donc
  // rejouer cette migration ne duplique jamais rien).
  useEffect(() => {
    if (representedSellers.length === 0) { setDealFormsBySeller({}); return; }
    let cancelled = false;
    (async () => {
      const result: Record<string, DealFormEntry[]> = {};
      for (const seller of representedSellers) {
        try {
          const legacyTokens: Array<{ token: string; origin: 'broker' | 'shared' }> = [];
          if ((seller as any).brokerDraftToken) legacyTokens.push({ token: (seller as any).brokerDraftToken, origin: 'broker' });
          if ((seller as any).formToken) legacyTokens.push({ token: (seller as any).formToken, origin: 'shared' });
          for (const legacy of legacyTokens) {
            const legacyRef = doc(db, 'representedSellers', seller.id, 'dealForms', legacy.token);
            const legacySnap = await getDoc(legacyRef);
            if (!legacySnap.exists()) {
              await setDoc(legacyRef, {
                token: legacy.token,
                origin: legacy.origin,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
            }
          }

          const entriesSnap = await getDocs(collection(db, 'representedSellers', seller.id, 'dealForms'));
          const entries: DealFormEntry[] = [];
          for (const entryDoc of entriesSnap.docs) {
            const data = entryDoc.data() as any;
            const entry: DealFormEntry = {
              id: entryDoc.id,
              token: data.token,
              origin: data.origin,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
              copiedFromFormId: data.copiedFromFormId,
              openedAsFormId: data.openedAsFormId,
              finalizedDealId: data.finalizedDealId,
              finalizedAt: data.finalizedAt,
            };
            if (!entry.finalizedDealId) {
              try {
                const draftSnap = await getDoc(doc(db, 'dealFormDrafts', entry.token));
                if (draftSnap.exists()) {
                  const draftData = draftSnap.data() as any;
                  entry.liveStatus = draftData.status;
                  entry.liveBusinessName = draftData.formData?.general?.legalName || draftData.formData?.general?.tradeName || '';
                }
              } catch { /* entrée orpheline ou permission — ignorée silencieusement */ }
            }
            entries.push(entry);
          }
          entries.sort((a, b) => (a.updatedAt?.toMillis?.() || 0) < (b.updatedAt?.toMillis?.() || 0) ? 1 : -1);
          result[seller.id] = entries;
        } catch (err) {
          console.error(`Error loading dealForms for seller ${seller.id}:`, err);
          result[seller.id] = [];
        }
      }
      if (!cancelled) setDealFormsBySeller(result);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [representedSellers, dealFormsRefreshKey]);

  const loadBrokerDashboard = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      // Load broker's listings (deals submitted by broker)
      const dealsRef = collection(db, "deals");
      const dealsQuery = query(
        dealsRef,
        where("brokerId", "==", user.id),
        orderBy("createdAt", "desc")
      );

      const dealsSnapshot = await getDocs(dealsQuery);
      const dealsData = dealsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BrokerDeal[];

      // Demo listings from static listing data for testing
      const demoListings: BrokerDeal[] = LISTING_DETAILS.map((listing, index) => {
        const content = listing[language as 'en' | 'fr'] || listing.en;
        return {
          id: listing.id,
          businessName: content.title,
          businessDescription: content.description?.[0] || '',
          askingPrice: parseFloat(content.financials.askingPrice.replace(/[^0-9.]/g, '')) || 500000,
          currency: 'USD',
          status: 'active',
          createdAt: { toDate: () => new Date(Date.now() - (index + 1) * 86400000 * 7) },
          brokerId: user.id,
          brokerCommission: 5,
          commissionType: 'percentage' as const,
          dealStatus: (['listed', 'under_negotiation', 'listed', 'listed', 'under_negotiation', 'listed'] as const)[index % 6],
          kpis: {
            views: 120 + index * 35,
            inquiries: 8 + index * 3,
            ndaRequests: 4 + index,
            ndaSigned: 2 + Math.floor(index / 2),
            loiReceived: index % 3 === 0 ? 1 : 0,
            meetingsScheduled: 1 + (index % 3),
            averageResponseTime: 2.5,
            daysOnMarket: 30 + index * 10,
          },
        };
      });

      // Merge: real data takes priority over demo
      const realDealIds = new Set(dealsData.map(d => d.id));
      const mergedListings = [
        ...dealsData,
        ...demoListings.filter(dl => !realDealIds.has(dl.id)),
      ];
      setMyListings(mergedListings);

          // representedSellers est maintenant chargé via un onSnapshot dédié
      // (voir le useEffect séparé plus bas) pour refléter en temps réel
      // les changements de statut de formulaire (formStatus) sans que le
      // broker ait besoin de recharger la page.

      // Deals dealSubmissions en attente d'approbation vendeur, pour
      // affichage de la bannière CIM en lecture seule (statut + "Voir le
      // CIM" + "Demander des modifications" — jamais "Approuver", réservé
      // au vrai vendeur, voir deal-approval.service.ts sellerApprove()).
      try {
        const pendingApprovalQuery = query(
          collection(db, "dealSubmissions"),
          where("brokerId", "==", user.id),
          where("approvalPending", "==", true)
        );
        const pendingApprovalSnap = await getDocs(pendingApprovalQuery);
        setPendingApprovalDeals(
          pendingApprovalSnap.docs.map(d => ({ id: d.id, ...d.data() })) as PendingApprovalDeal[]
        );
      } catch (pendingErr) {
        console.error("Error loading pending approval deals:", pendingErr);
        setPendingApprovalDeals([]);
      }

      // Calculate total KPIs
      const kpis = calculateTotalKPIs(dealsData);
      setTotalKPIs(kpis);

    } catch (error) {
      console.error("Error loading broker dashboard:", error);
      toast({
        title: t.errorLoading,
        description: t.failedToLoad,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const calculateTotalKPIs = (deals: BrokerDeal[]) => {
    return {
      totalDeals: deals.length,
      activeDeals: deals.filter(d => d.dealStatus === 'listed').length,
      underNegotiation: deals.filter(d => d.dealStatus === 'under_negotiation').length,
      closedDeals: deals.filter(d => d.dealStatus === 'closed').length,
      totalViews: deals.reduce((sum, d) => sum + (d.kpis?.views || 0), 0),
      totalInquiries: deals.reduce((sum, d) => sum + (d.kpis?.inquiries || 0), 0),
      totalNDAs: deals.reduce((sum, d) => sum + (d.kpis?.ndaSigned || 0), 0),
      totalLOIs: deals.reduce((sum, d) => sum + (d.kpis?.loiReceived || 0), 0),
      totalCommissionEarned: deals.filter(d => d.dealStatus === 'closed').reduce((sum, d) => sum + d.brokerCommission, 0),
      totalCommissionPending: deals.filter(d => d.dealStatus === 'under_negotiation').reduce((sum, d) => sum + d.brokerCommission, 0),
      avgDaysOnMarket: deals.length > 0 ? deals.reduce((sum, d) => sum + (d.kpis?.daysOnMarket || 0), 0) / deals.length : 0,
      avgResponseTime: deals.length > 0 ? deals.reduce((sum, d) => sum + (d.kpis?.averageResponseTime || 0), 0) / deals.length : 0
    };
  };

  // AJOUTÉ — génère un identifiant unique pour les tokens de formulaire.
  // crypto.randomUUID() exige un contexte sécurisé (HTTPS ou localhost)
  // — indisponible sur une IP locale en HTTP simple (ex. 10.0.0.204),
  // couramment utilisée en développement. Repli manuel sinon, format
  // UUID v4 standard.
  const generateUUID = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  // AJOUTÉ — construit l'URL d'invitation pour un vendeur donné (même
  // pattern que celui utilisé dans addRepresentedSeller() ci-dessous).
 const buildInviteUrl = (sellerId: string) =>
  `${window.location.origin}/register/sellers?repId=${sellerId}&brokerId=${user?.id}`;

  // AJOUTÉ — bascule vers "Nouvelle Annonce" en reprenant une entrée
  // précise (voir SubmitDealForm.tsx brokerMode/resumeSellerId/
  // resumeFormId) — exactement où le broker s'était arrêté sur CETTE
  // compagnie précise.
  const handleResumeDraft = (seller: RepresentedSeller, entry: DealFormEntry) => {
    setResumeSellerId(seller.id);
    setResumeFormId(entry.token);
    setActiveTab('list-business');
  };

  // AJOUTÉ — démarre un formulaire strictement vierge, peu importe le
  // dernier deal travaillé. C'est le correctif du bug rapporté : avant,
  // "Nouvelle Annonce" pouvait réafficher les données du deal précédent.
  const handleNewListing = () => {
    setResumeSellerId(null);
    setResumeFormId(null);
    setActiveTab('list-business');
  };

  // AJOUTÉ — envoi/renvoi de l'invitation, appelable indépendamment de
  // l'ajout du vendeur. Réutilise sendSellerInviteEmail comme
  // addRepresentedSeller(), mais écrit aussi inviteSentAt/inviteStatus/
  // inviteError sur le document representedSellers pour que l'échec ne
  // soit plus seulement visible en console.warn — l'UI peut désormais
  // afficher un avertissement explicite si le dernier envoi a échoué.
  const handleResendInvite = async (seller: RepresentedSeller) => {
    if (!user) return;
    setInvitingSellerId(seller.id);
    const inviteUrl = buildInviteUrl(seller.id);
    try {
      const functions = getFunctions();
      const sendSellerInviteEmail = httpsCallable(functions, "sendSellerInviteEmail");
      await sendSellerInviteEmail({
        email: seller.email,
        sellerFirstName: seller.name?.split(" ")[0] || "",
        brokerName: user.displayName || user.email,
        language,
        repId: seller.id,
        brokerId: user.id,
        inviteUrl,
      });

      await updateDoc(doc(db, "representedSellers", seller.id), {
        inviteSentAt: serverTimestamp(),
        inviteStatus: "sent",
        inviteError: null,
      });

      toast({ title: t.inviteSent, description: t.inviteSentDesc });
      await loadBrokerDashboard();
    } catch (error: any) {
      console.warn("[BrokerDashboard] handleResendInvite failed:", error);
      try {
        await updateDoc(doc(db, "representedSellers", seller.id), {
          inviteSentAt: serverTimestamp(),
          inviteStatus: "failed",
          inviteError: error?.message || String(error),
        });
      } catch { /* non-bloquant */ }

      toast({
        title: t.inviteFailed,
        description: t.inviteFailedDesc,
        variant: "destructive",
      });
      await loadBrokerDashboard();
    } finally {
      setInvitingSellerId(null);
    }
  };

  // AJOUTÉ — copie le lien d'invitation dans le presse-papiers, filet de
  // sécurité indépendant de l'email (domaine d'envoi, filtre spam, etc.).
  const handleCopyInviteLink = async (seller: RepresentedSeller) => {
    const url = buildInviteUrl(seller.id);
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: t.linkCopied, description: t.linkCopiedDesc });
    } catch {
      toast({
        title: t.linkCopied,
        description: url, // fallback : affiche l'URL si l'API clipboard échoue (contexte non sécurisé, permissions)
      });
    }
  };

  // AJOUTÉ — partage/repartage du formulaire de deal (dealFormDrafts).
  // Réutilise le même token tant que le formulaire n'est pas encore
  // soumis (le broker peut cliquer "Repartager" autant de fois que
  // nécessaire sans jamais invalider un lien déjà envoyé) ; génère un
  // nouveau token seulement si le précédent est déjà 'submitted' —
  // utile si un second deal doit être monté plus tard pour ce même
  // vendeur représenté.
  const [sharingFormSellerId, setSharingFormSellerId] = useState<string | null>(null);
  // AJOUTÉ — dialogue affichant le lien du formulaire de façon visible
  // et sélectionnable (remplace le toast, qui disparaît tout seul et
  // dont la copie automatique peut échouer selon le contexte du
  // navigateur — ex. IP locale en HTTP, sans crypto.subtle disponible).
  const [shareFormDialogUrl, setShareFormDialogUrl] = useState<string | null>(null);
  const [shareFormDialogCopied, setShareFormDialogCopied] = useState(false);

  const handleCopyFromDialog = async () => {
    if (!shareFormDialogUrl) return;
    let succeeded = false;
    try {
      await navigator.clipboard.writeText(shareFormDialogUrl);
      succeeded = true;
    } catch {
      const input = document.getElementById('share-form-url-input') as HTMLInputElement | null;
      input?.select();
    }
    if (succeeded) {
      setShareFormDialogCopied(true);
      setTimeout(() => setShareFormDialogCopied(false), 2000);
    } else {
      toast({
        title: language === 'fr' ? 'Copie automatique indisponible' : 'Automatic copy unavailable',
        description: language === 'fr'
          ? 'Le texte est sélectionné — utilisez Ctrl+C (ou Cmd+C) pour le copier manuellement.'
          : 'The text is selected — use Ctrl+C (or Cmd+C) to copy it manually.',
      });
    }
  };

  // AJOUTÉ — retire une entrée (brouillon abandonné, duplicata créé par
  // erreur, etc.). Supprime l'index dealForms ET le document dealFormDrafts
  // sous-jacent (autorisé : brokerId == auth.uid dans les deux cas).
  const handleDiscardEntry = async (seller: RepresentedSeller, entry: DealFormEntry) => {    const confirmMsg = language === 'fr'
      ? 'Retirer ce formulaire ? Cette action est irréversible.'
      : 'Discard this form? This action cannot be undone.';
    if (!window.confirm(confirmMsg)) return;
    try {
      await deleteDoc(doc(db, 'representedSellers', seller.id, 'dealForms', entry.id));
      try { await deleteDoc(doc(db, 'dealFormDrafts', entry.token)); } catch { /* déjà supprimé ou permission — non bloquant */ }
      refreshDealForms();
    } catch (error) {
      console.error('Error discarding deal form entry:', error);
      toast({ title: t.errorLoading, description: t.failedToLoad, variant: 'destructive' });
    }
  };

  // AJOUTÉ — télécharge une copie PDF du formulaire de soumission brut
  // pour un deal donné, pour dossier client / référence ultérieure. Voir
  // deal-form-pdf.ts pour la mise en garde sur les deals créés via
  // l'ancien chemin de soumission (rawFormData potentiellement absent).
  const [downloadingDealId, setDownloadingDealId] = useState<string | null>(null);
  const handleDownloadDealFormPdf = async (dealId: string) => {
    setDownloadingDealId(dealId);
    try {
      await downloadDealFormPdf(dealId, language as 'en' | 'fr');
    } catch (error: any) {
      console.error('Error downloading deal form PDF:', error);
      toast({
        title: t.errorLoading,
        description: error?.message || t.failedToLoad,
        variant: 'destructive',
      });
    } finally {
      setDownloadingDealId(null);
    }
  };

  const handleShareForm = async (seller: RepresentedSeller, existingEntry?: DealFormEntry) => {
    if (!user) return;
    setSharingFormSellerId(seller.id);
    try {
      // AJOUTÉ — deux cas distincts maintenant :
      //  - repartage d'une entrée EXISTANTE non soumise (même token,
      //    juste rafraîchir l'horodatage) ;
      //  - "+ Partager un nouveau formulaire vierge" : TOUJOURS une
      //    NOUVELLE entrée, distincte des précédentes — un vendeur peut
      //    avoir plusieurs compagnies en cours en parallèle, on ne réutilise
      //    donc plus jamais implicitement le dernier token connu.
      const isReshare = !!existingEntry && existingEntry.liveStatus !== 'submitted';
      const token = isReshare ? existingEntry!.token : generateUUID();

      if (!isReshare) {
        await setDoc(doc(db, 'dealFormDrafts', token), {
          token,
          brokerId: user.id,
          repId: seller.id,
          sellerName: seller.name,
          sellerEmail: seller.email,
          status: 'pending',
          formData: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          submittedAt: null,
          revoked: false,
        });
        await setDoc(doc(db, 'representedSellers', seller.id, 'dealForms', token), {
          token,
          origin: 'shared',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        await updateDoc(doc(db, 'dealFormDrafts', token), { updatedAt: serverTimestamp() });
        await updateDoc(doc(db, 'representedSellers', seller.id, 'dealForms', token), { updatedAt: serverTimestamp() });
      }

      const url = `${window.location.origin}/deal-form/${token}`;
      // Tentative de copie automatique en silence — non bloquante : le
      // dialogue ci-dessous affiche de toute façon le lien avec un
      // bouton de copie manuel, donc l'échec de cette tentative
      // silencieuse (fréquent sur IP locale en HTTP, contexte non
      // sécurisé) ne prive jamais le broker d'un moyen de copier le lien.
      try { await navigator.clipboard.writeText(url); } catch { /* ignoré, voir dialogue */ }
      setShareFormDialogUrl(url);
      refreshDealForms();
    } catch (error) {
      console.error("Error sharing deal form:", error);
      toast({ title: t.errorLoading, description: t.failedToLoad, variant: "destructive" });
    } finally {
      setSharingFormSellerId(null);
    }
  };

  // AJOUTÉ — le broker ouvre le formulaire complété par le vendeur dans
  // son propre SubmitDealForm (mode normal, éditable), pré-rempli avec
  // les données du brouillon. Charge dealFormDrafts/{token}.formData
  // dans localStorage sous la même clé que saveDraft() écrit
  // normalement (dealSubmissionDraft) — SubmitDealForm.tsx la relit déjà
  // au montage (voir le useEffect de restauration ajouté précédemment),
  // donc aucune modification supplémentaire n'est requise côté formulaire
  // pour ce chemin précis : le broker atterrit sur /broker/... et
  // retrouve le formulaire du vendeur comme s'il l'avait sauvegardé
  // lui-même.
  //
  // CORRIGÉ — ce chemin (ouvrir le formulaire SOUMIS par le vendeur pour
  // que le broker le complète et le paie) reste volontairement en dehors
  // du nouveau système brokerDraftToken/resumeSellerId : ce n'est pas "un
  // brouillon en cours du broker", c'est un formulaire déjà approuvé par
  // le vendeur, sur le point d'être finalisé. On passe donc toujours par
  // localStorage puis "Nouvelle Annonce" (resumeSellerId à null), pas par
  // handleResumeDraft().
  const handleOpenSellerForm = async (seller: RepresentedSeller, entry: DealFormEntry) => {
    try {
      // AJOUTÉ — réutilise la copie déjà créée si le broker avait déjà
      // cliqué "Ouvrir" pour CETTE MÊME entrée précédemment — évite de
      // dupliquer une nouvelle copie à chaque clic répété.
      if (entry.openedAsFormId) {
        setResumeSellerId(seller.id);
        setResumeFormId(entry.openedAsFormId);
        setActiveTab('list-business');
        return;
      }

      const draftSnap = await getDoc(doc(db, 'dealFormDrafts', entry.token));
      if (!draftSnap.exists() || !draftSnap.data()?.formData) {
        toast({ title: t.errorLoading, description: t.failedToLoad, variant: "destructive" });
        return;
      }
      const d: any = draftSnap.data();

      // CORRIGÉ — le document dealFormDrafts soumis par le vendeur
      // (status: 'submitted') est IMMUABLE côté règle Firestore une fois
      // soumis (allow update: if resource.data.status != 'submitted' ...)
      // — le broker ne peut plus y écrire pour continuer à le remplir. On
      // copie donc ses données dans une NOUVELLE entrée (origin: 'broker',
      // copiedFromFormId pointant vers l'originale), éditable et
      // sauvegardable, en laissant le document soumis par le vendeur
      // intact comme trace historique. openedAsFormId sur l'entrée
      // originale évite de recréer une nouvelle copie à chaque clic futur.
      const newToken = generateUUID();
      await setDoc(doc(db, 'dealFormDrafts', newToken), {
        token: newToken,
        authorRole: 'broker',
        brokerId: user?.id || null,
        repId: seller.id,
        formData: d.formData,
        uploadedFiles: d.uploadedFiles || { documents: [], photos: [], logo: null },
        analyzedDocs: d.analyzedDocs || [],
        status: 'in_progress',
        revoked: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await setDoc(doc(db, 'representedSellers', seller.id, 'dealForms', newToken), {
        token: newToken,
        origin: 'broker',
        copiedFromFormId: entry.token,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'representedSellers', seller.id, 'dealForms', entry.token), {
        openedAsFormId: newToken,
        updatedAt: serverTimestamp(),
      });

      setResumeSellerId(seller.id);
      setResumeFormId(newToken);
      setActiveTab('list-business');
      refreshDealForms();
      toast({
        title: language === 'fr' ? 'Formulaire chargé' : 'Form loaded',
        description: language === 'fr'
          ? "Le formulaire du vendeur a été chargé. Complétez et soumettez."
          : "The seller's form has been loaded. Complete and submit.",
      });
    } catch (error) {
      console.error("Error opening seller form:", error);
      toast({ title: t.errorLoading, description: t.failedToLoad, variant: "destructive" });
    }
  };

  // AJOUTÉ — sauvegarde des modifications d'un vendeur représenté existant
  const handleSaveEditSeller = async (updates: Partial<RepresentedSeller>) => {
    if (!editingSeller) return;
    try {
      await updateDoc(doc(db, "representedSellers", editingSeller.id), {
        name: updates.name,
        company: updates.company,
        email: updates.email,
        phone: updates.phone,
        updatedAt: serverTimestamp(),
      });
      toast({ title: t.sellerUpdated, description: t.sellerUpdatedDesc });
      setEditingSeller(null);
      await loadBrokerDashboard();
    } catch (error) {
      console.error("Error updating seller:", error);
      toast({ title: t.errorLoading, description: t.failedToLoad, variant: "destructive" });
    }
  };

  // AJOUTÉ — ouvre la boîte de dialogue de suppression après avoir vérifié
  // (option A retenue) si des deals sont déjà rattachés à ce vendeur. La
  // vérification porte sur dealSubmissions.sellerId, le champ fiable
  // introduit par le correctif Broker→Vendeur (voir deals.service.ts
  // submitDeal()) — pas sur myListings, qui vient du miroir 'deals' et
  // peut être désynchronisé.
   const handleRequestDeleteSeller = async (seller: RepresentedSeller) => {
    setDeletingSeller(seller);
    setCheckingDeleteEligibility(true);
    try {
      // CORRIGÉ — filtrer par brokerId (correspond à la règle Firestore :
      // le broker connecté cherche SES PROPRES deals, brokerId ==
      // request.auth.uid), puis filtrer sellerId côté client sur les
      // résultats déjà reçus. La requête originale filtrait directement
      // sur sellerId == seller.id (l'id du VENDEUR, pas de l'utilisateur
      // connecté), ce qui ne correspond à aucune condition de la règle
      // et se faisait refuser avant même de savoir s'il y avait des
      // résultats.
      if (!user) throw new Error("Not authenticated");
      const dealsQuery = query(
        collection(db, "dealSubmissions"),
        where("brokerId", "==", user.id)
      );
      const snap = await getDocs(dealsQuery);
      const matchingDeals = snap.docs.filter(d => d.data().sellerId === seller.id);
      setDeletingSellerDealCount(matchingDeals.length);
    } catch (error) {
      console.error("Error checking deals before delete:", error);
      // Par prudence, en cas d'échec de la vérification, on bloque la
      // suppression plutôt que de risquer de supprimer un vendeur ayant
      // réellement des deals actifs.
      setDeletingSellerDealCount(1);
    } finally {
      setCheckingDeleteEligibility(false);
    }
  };

  // AJOUTÉ — suppression logique (isActive: false), jamais physique :
  // préserve l'historique si jamais des deals étaient créés après coup,
  // et garde une trace pour le staff en cas de besoin.
  const handleConfirmDeleteSeller = async () => {
    if (!deletingSeller || deletingSellerDealCount > 0) return;
    try {
      await updateDoc(doc(db, "representedSellers", deletingSeller.id), {
        isActive: false,
        deletedAt: serverTimestamp(),
      });
      toast({ title: t.sellerDeleted, description: t.sellerDeletedDesc });
      setDeletingSeller(null);
      await loadBrokerDashboard();
    } catch (error) {
      console.error("Error deleting seller:", error);
      toast({ title: t.errorLoading, description: t.failedToLoad, variant: "destructive" });
    }
  };

  const addRepresentedSeller = async (sellerData: Partial<RepresentedSeller>) => {
    if (!user) return;

    try {
           const sellerRef = await addDoc(collection(db, "representedSellers"), {
        ...sellerData,
        brokerId: user.id,
        brokerName: user.displayName || user.email,
        isActive: true,
        // corrigé — devient true seulement quand le vendeur crée réellement
        // son compte (voir RegisterSellers.tsx / register/seller invité).
        // Avant ce correctif, ce champ mentait dès la création : il valait
        // true alors qu'aucun compte n'existait encore.
        hasPortalAccess: false,
        // AJOUTÉ — nécessaire pour que la règle Firestore
        // resource.data.uid == null évalue correctement à la liaison de
        // compte : un champ ABSENT du document ≠ un champ explicitement
        // NULL en Security Rules (le premier cause une erreur
        // d'évaluation silencieuse, jamais "true"). Cause racine du bug
        // où hasPortalAccess restait bloqué à false malgré une
        // inscription vendeur réussie.
        uid: null,
        addedAt: serverTimestamp(),
        representationAgreement: {
          signed: false
        }
      });


      await createMergedCalendar(user.id, sellerRef.id);

      // Lien d'invitation — même pattern que dealSourcerRef, lu par
      // RegisterSellers.tsx (register/seller invité) au moment de la
      // création du compte pour relier representedSellers/{repId} au
      // vrai uid et activer hasPortalAccess.
      const inviteUrl = buildInviteUrl(sellerRef.id);

      // Envoi de l'email d'invitation (non bloquant — un échec d'email ne
      // doit pas empêcher l'ajout du vendeur représenté ni afficher une
      // erreur trompeuse à l'utilisatrice). Le résultat est maintenant
      // aussi écrit sur le document (inviteSentAt/inviteStatus/inviteError)
      // pour que l'échec soit visible dans l'UI, pas seulement en console.
      try {
        const functions = getFunctions();
        const sendSellerInviteEmail = httpsCallable(functions, "sendSellerInviteEmail");
        await sendSellerInviteEmail({
          email: sellerData.email,
          sellerFirstName: sellerData.name?.split(" ")[0] || "",
          brokerName: user.displayName || user.email,
          language,
          repId: sellerRef.id,
          brokerId: user.id,
          inviteUrl,
        });
        await updateDoc(sellerRef, {
          inviteSentAt: serverTimestamp(),
          inviteStatus: "sent",
          inviteError: null,
        });
      } catch (emailError: any) {
        console.warn("[BrokerDashboard] sendSellerInviteEmail failed (non-blocking):", emailError);
        try {
          await updateDoc(sellerRef, {
            inviteSentAt: serverTimestamp(),
            inviteStatus: "failed",
            inviteError: emailError?.message || String(emailError),
          });
        } catch { /* non-bloquant */ }
      }

      toast({
        title: t.sellerAdded,
        description: t.sellerAddedDesc
      });

      setShowAddSeller(false);
      await loadBrokerDashboard();
    } catch (error) {
      console.error("Error adding seller:", error);
      toast({
        title: t.errorLoading,
        description: t.failedToAddSeller,
        variant: "destructive"
      });
    }
  };

  const createMergedCalendar = async (brokerId: string, sellerId: string) => {
    try {
      const mergedCalendarRef = await addDoc(collection(db, "mergedCalendars"), {
        brokerId,
        sellerId,
        syncEnabled: true,
        createdAt: serverTimestamp(),
        brokerAvailability: [],
        sellerAvailability: []
      });

      const mergedCalendarUrl = `${window.location.origin}/calendar/merged/${mergedCalendarRef.id}`;

      await updateDoc(mergedCalendarRef, {
        mergedCalendarUrl
      });

      return mergedCalendarUrl;
    } catch (error) {
      console.error("Error creating merged calendar:", error);
      throw error;
    }
  };

  const formatCurrency = (amount: number, currency: string = "USD") => {
    return new Intl.NumberFormat(language === 'fr' ? 'fr-CA' : 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex">
        <BrokerSidebar activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="flex-1 flex flex-col">
          <DashboardHeader />
          <main className="flex-1 p-6 bg-muted/30 flex items-center justify-center">
            <p className="text-muted-foreground">{t.loadingDashboard}</p>
          </main>
        </div>
      </div>
    );
  }

    const getDisplayStatus = (deal: any): string => {
    return deal.dealStatus || deal.status || deal.currentStage || 'submission';
  };

  return (
    <div className="min-h-screen bg-background flex">
      <BrokerSidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <div className="flex-1 flex flex-col">
        <DashboardHeader />
        <main className="flex-1 p-6 bg-muted/30 overflow-y-auto">
          <div className="space-y-6">
            {/* Header with Language Selector */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
                <p className="text-muted-foreground mt-2">{t.subtitle}</p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={() => setShowAddSeller(true)}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  {t.addSeller}
                </Button>
                {/* CORRIGÉ — passe par handleNewListing (réinitialise
                    resumeSellerId à null) plutôt que setActiveTab direct,
                    pour garantir un formulaire vierge à chaque clic. */}
                <Button onClick={handleNewListing}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t.listNewBusiness}
                </Button>
              </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{totalKPIs.totalDeals || 0}</div>
                  <p className="text-xs text-muted-foreground">{t.totalListings}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-green-600">{totalKPIs.activeDeals || 0}</div>
                  <p className="text-xs text-muted-foreground">{t.active}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-orange-600">{totalKPIs.underNegotiation || 0}</div>
                  <p className="text-xs text-muted-foreground">{t.underNegotiation}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-blue-600">{totalKPIs.closedDeals || 0}</div>
                  <p className="text-xs text-muted-foreground">{t.closedDeals}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{totalKPIs.totalViews || 0}</div>
                  <p className="text-xs text-muted-foreground">{t.totalViews}</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    <div className="text-2xl font-bold text-primary">{totalKPIs.totalNDAs || 0}</div>
                  </div>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'NDA Signés' : 'NDAs Signed'}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{totalKPIs.totalLOIs || 0}</div>
                  <p className="text-xs text-muted-foreground">{t.loiReceived}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-green-600">{formatCurrency(totalKPIs.totalCommissionEarned || 0)}</div>
                  <p className="text-xs text-muted-foreground">{t.commissionEarned}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-orange-600">{formatCurrency(totalKPIs.totalCommissionPending || 0)}</div>
                  <p className="text-xs text-muted-foreground">{t.commissionPending}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{Math.round(totalKPIs.avgDaysOnMarket || 0)} {t.days}</div>
                  <p className="text-xs text-muted-foreground">{t.avgDaysOnMarket}</p>
                </CardContent>
              </Card>
            </div>

            {/* Tabs Content - controlled by sidebar */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <OnboardingGuide
            role="broker"
            language={language as 'en' | 'fr'}
            onTabChange={setActiveTab}
          />
          <div className="grid md:grid-cols-2 gap-6">
            {/* Top Performing Listings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  {t.topPerformingListings}
                </CardTitle>
                <CardDescription>{t.basedOnViewsAndInquiries}</CardDescription>
              </CardHeader>
              <CardContent>
                {myListings.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">{t.noListingsYet}</p>
                ) : (
                  <div className="space-y-4">
                    {myListings
                      .sort((a, b) => (b.kpis?.views || 0) - (a.kpis?.views || 0))
                      .slice(0, 5)
                      .map(deal => (
                        <div key={deal.id} className="border rounded-lg p-4 space-y-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold">{deal.businessName}</h4>
                              <p className="text-sm text-muted-foreground">{deal.representedSeller?.name}</p>
                            </div>
                           <Badge variant={
                              deal.dealStatus === 'listed' ? 'default' :
                              deal.dealStatus === 'under_negotiation' ? 'secondary' : 'outline'
                            }>
                              {getDisplayStatus(deal)}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <div className="text-lg font-bold">{deal.kpis?.views || 0}</div>
                              <div className="text-xs text-muted-foreground">{t.views}</div>
                            </div>
                            <div>
                              <div className="text-lg font-bold">{deal.kpis?.inquiries || 0}</div>
                              <div className="text-xs text-muted-foreground">{t.inquiries}</div>
                            </div>
                            <div>
                              <div className="text-lg font-bold">{deal.kpis?.loiReceived || 0}</div>
                              <div className="text-xs text-muted-foreground">{t.lois}</div>
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full"
                            onClick={() => {
                              setSelectedDeal(deal.id);
                              setActiveTab("my-listings");
                            }}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            {t.viewDetails}
                          </Button>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Represented Sellers Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {t.representedSellers} ({representedSellers.length})
                </CardTitle>
                <CardDescription>{t.sellersYouRepresent}</CardDescription>
              </CardHeader>
              <CardContent>
                {representedSellers.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">{t.noRepresentedSellers}</p>
                    <Button variant="outline" onClick={() => setShowAddSeller(true)}>
                      <UserPlus className="mr-2 h-4 w-4" />
                      {t.addYourFirstSeller}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {representedSellers.slice(0, 5).map(seller => (
                      <div key={seller.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarFallback>{seller.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{seller.name}</p>
                            <p className="text-sm text-muted-foreground">{seller.company}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex gap-2 mb-1">
                            <Badge variant={seller.hasPortalAccess ? "default" : "outline"} className="text-xs">
                              {seller.hasPortalAccess ? t.activeStatus : "Pending"}
                            </Badge>
                          </div>
                           <p className="text-xs text-muted-foreground">
                            {t.activeDeals} {myListings.filter(d => (d as any).sellerId === seller.id).length}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Compact Calendar Widget */}
            <CompactCalendarWidget />
          </div>
        </TabsContent>

        {/* Contact Requests Tab */}
        <TabsContent value="contact-requests" className="space-y-4">
          <SellerContactRequests language={language} />
        </TabsContent>

        {/* My Listings Tab */}
        <TabsContent value="my-listings" className="space-y-4">
          {/* Bannière CIM en lecture seule — deals dealSubmissions du broker
              en attente d'approbation vendeur. Le broker voit le statut,
              peut consulter le CIM et demander des modifications, mais ne
              peut jamais approuver/payer à la place du vendeur (voir
              deal-approval.service.ts sellerApprove()). */}
          {pendingApprovalDeals.length > 0 && (
            <div className="space-y-3">
              {pendingApprovalDeals.map(deal => (
                <CIMApprovalBanner
                  key={deal.id}
                  dealId={deal.id}
                  businessName={deal.businessName || deal.legalName}
                  readOnly
                  onChangesRequested={() => loadBrokerDashboard()}
                />
              ))}
            </div>
          )}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t.myListings}</CardTitle>
                <CardDescription>{t.allBusinesses}</CardDescription>
              </div>
              {/* CORRIGÉ — même correctif que le bouton d'en-tête :
                  handleNewListing garantit un formulaire vierge. */}
              <Button onClick={handleNewListing}>
                <Plus className="mr-2 h-4 w-4" />
                {t.newListing}
              </Button>
            </CardHeader>
            <CardContent>
              {myListings.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">{t.noListingsYet}</p>
                  <Button onClick={handleNewListing}>
                    {t.createFirstListing}
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                                    {representedSellers.map(seller => {
                    const sellerDeals = myListings.filter(d => (d as any).sellerId === seller.id);
                    if (sellerDeals.length === 0) return null;

                    return (
                      <div key={seller.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarFallback>{seller.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <div>
                              <h3 className="font-semibold">{seller.name}</h3>
                              <p className="text-sm text-muted-foreground">
                                {sellerDeals.length} {sellerDeals.length !== 1 ? t.listings : t.listing}
                              </p>
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => window.open(`/calendar/merged/${seller.calendarId}`, '_blank')}
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {t.viewMergedCalendar}
                          </Button>
                        </div>

                        <div className="space-y-4">
                          {sellerDeals.map(deal => (
                            
                            <Card
                              key={deal.id}
                              className="cursor-pointer hover:shadow-md hover:border-primary/40 transition-all"
                              onClick={() => navigate(`/broker/edit-deal/${deal.id}`)}
                            >
                              <CardContent className="pt-4">

                                <div className="flex items-start justify-between mb-4">
                                  <div>
                                    <h4 className="font-semibold text-lg">{deal.businessName}</h4>
                                    <p className="text-sm text-muted-foreground">
                                      {deal.businessDescription?.substring(0, 150)}...
                                    </p>
                                  </div>
                                 <Badge>{getDisplayStatus(deal).replace(/_/g, ' ')}</Badge>
                                </div>

                                <div className="grid grid-cols-4 gap-4 mb-4">
                                  <div>
                                    <p className="text-sm text-muted-foreground">{t.askingPrice}</p>
                                    <p className="font-semibold">{formatCurrency(deal.askingPrice, deal.currency)}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-muted-foreground">{t.commission}</p>
                                    <p className="font-semibold">
                                      {deal.commissionType === 'percentage'
                                        ? `${deal.brokerCommission}%`
                                        : formatCurrency(deal.brokerCommission)
                                      }
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-muted-foreground">{t.daysOnMarket}</p>
                                    <p className="font-semibold">{deal.kpis?.daysOnMarket || 0}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-muted-foreground">{t.status}</p>
                                    <p className="font-semibold">{deal.status}</p>
                                  </div>
                                </div>

                                <div className="bg-muted/50 rounded-lg p-4 mb-4">
                                  <div className="flex items-center gap-2 mb-2">
                                    <BarChart3 className="h-4 w-4" />
                                    <span className="font-medium">{t.performanceMetrics}</span>
                                  </div>
                                  <div className="grid grid-cols-5 gap-4 text-center">
                                    <div>
                                      <div className="text-xl font-bold">{deal.kpis?.views || 0}</div>
                                      <div className="text-xs text-muted-foreground">{t.views}</div>
                                    </div>
                                    <div>
                                      <div className="text-xl font-bold">{deal.kpis?.inquiries || 0}</div>
                                      <div className="text-xs text-muted-foreground">{t.inquiries}</div>
                                    </div>
                                    <div>
                                      <div className="text-xl font-bold">{deal.kpis?.ndaSigned || 0}</div>
                                      <div className="text-xs text-muted-foreground">{t.ndas}</div>
                                    </div>
                                    <div>
                                      <div className="text-xl font-bold">{deal.kpis?.loiReceived || 0}</div>
                                      <div className="text-xs text-muted-foreground">{t.lois}</div>
                                    </div>
                                    <div>
                                      <div className="text-xl font-bold">{deal.kpis?.meetingsScheduled || 0}</div>
                                      <div className="text-xs text-muted-foreground">{t.meetings}</div>
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg mb-4">
                                  <Shield className="h-5 w-5 text-blue-600" />
                                  <div>
                                    <p className="font-medium text-sm">{t.sellerHasFullAccess}</p>
                                    <p className="text-xs text-muted-foreground">{seller.name} {t.canViewActivity}</p>
                                  </div>
                                </div>

                                <div className="flex gap-2">
                                                                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/deal-activity/${deal.id}`); }}>
                                    {t.viewActivityLog}
                                  </Button>
                                                                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/broker/edit-deal/${deal.id}`); }}>
                                    <Eye className="mr-2 h-4 w-4" />
                                    {t.manageListing}
                                  </Button>
                                                                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/deals/${deal.id}`); }}>
                                    {t.viewPublicPage}
                                  </Button>
                                                                   <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); window.open(`/calendar/merged/${seller.calendarId}`, '_blank'); }}>
                                    <Calendar className="mr-2 h-4 w-4" />
                                  </Button>
                                  <UpdateImpactDataDialog dealId={deal.id} />
                                </div>
                                <div className="mt-4">
                                  <SecureDocumentsSection dealId={deal.id} />
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Represented Sellers Tab */}
        <TabsContent value="sellers" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t.representedSellers}</CardTitle>
                <CardDescription>{t.managePortalAccess}</CardDescription>
              </div>
              <Button onClick={() => setShowAddSeller(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                {t.addSeller}
              </Button>
            </CardHeader>
            <CardContent>
              {representedSellers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">{t.noRepresentedSellers}</p>
                  <Button onClick={() => setShowAddSeller(true)}>
                    {t.addYourFirstSeller}
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                                   {representedSellers.map(seller => {
                    const sellerDeals = myListings.filter(d => (d as any).sellerId === seller.id);
                    const totalValue = sellerDeals.reduce((sum, d) => sum + (d.askingPrice || 0), 0);

                    return (
                      <Card key={seller.id}>
                        <CardHeader>
                          <div className="flex items-start gap-4">
                            <Avatar className="h-16 w-16">
                              <AvatarFallback className="text-lg">
                                {seller.name.substring(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <CardTitle className="text-xl">{seller.name}</CardTitle>
                              <CardDescription>{seller.company}</CardDescription>
                              <div className="flex gap-2 mt-2 flex-wrap">
                                <Badge variant={seller.hasPortalAccess ? "default" : "outline"}>
                                  {seller.hasPortalAccess ? t.portalActive : t.portalPending}
                                </Badge>
                                <Badge variant={seller.representationAgreement.signed ? "secondary" : "destructive"}>
                                  {seller.representationAgreement.signed ? t.agreementSigned : t.awaitingSig}
                                </Badge>
                                {/* AJOUTÉ — avertissement visible si le dernier envoi
                                    d'invitation a échoué (inviteStatus écrit par
                                    addRepresentedSeller()/handleResendInvite()) */}
                                {!seller.hasPortalAccess && seller.inviteStatus === "failed" && (
                                  <Badge variant="destructive" className="gap-1">
                                    <AlertCircle className="h-3 w-3" />
                                    {t.inviteFailedBadge}
                                  </Badge>
                                )}
                                {/* AJOUTÉ — badges d'en-tête dérivés de la liste des formulaires en
                                    cours (dealFormsBySeller), maintenant qu'un vendeur peut en avoir
                                    plusieurs simultanément — plus de champs singuliers à vérifier. */}
                                {(dealFormsBySeller[seller.id] || []).some(e => !e.finalizedDealId && e.origin === 'broker') && (
                                  <Badge variant="outline" className="gap-1">
                                    <FilePen className="h-3 w-3" />
                                    {t.draftInProgress}
                                  </Badge>
                                )}
                                {(dealFormsBySeller[seller.id] || []).some(e => !e.finalizedDealId && e.origin === 'shared' && e.liveStatus === 'submitted') && (
                                  <Badge className="gap-1" style={{ background: "rgba(206,171,101,0.15)", color: "#8a6d1f", border: "1px solid rgba(206,171,101,0.4)" }}>
                                    <CheckCircle2 className="h-3 w-3" />
                                    {t.formSubmittedBadge}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <p className="text-sm font-medium mb-1">{t.contact}</p>
                            <p className="text-sm text-muted-foreground">{seller.email}</p>
                            <p className="text-sm text-muted-foreground">{seller.phone}</p>
                          </div>

                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <div className="text-xl font-bold">{sellerDeals.length}</div>
                              <div className="text-xs text-muted-foreground">{t.active}</div>
                            </div>
                            <div>
                              <div className="text-xl font-bold">{formatCurrency(totalValue)}</div>
                              <div className="text-xs text-muted-foreground">{t.totalValue}</div>
                            </div>
                            <div>
                              <div className="text-xl font-bold">{seller.addedAt?.toDate?.()?.toLocaleDateString() || '-'}</div>
                              <div className="text-xs text-muted-foreground">{t.added}</div>
                            </div>
                          </div>

                          {seller.representationAgreement.signed && (
                            <div className="bg-muted/50 rounded-lg p-3">
                              <p className="text-sm font-medium mb-2">{t.agreementDetails}</p>
                              <div className="text-xs text-muted-foreground space-y-1">
                                <p>{t.signedDate} {seller.representationAgreement.signedDate?.toDate?.()?.toLocaleDateString()}</p>
                                <p>{t.expiresDate} {seller.representationAgreement.expiryDate?.toDate?.()?.toLocaleDateString()}</p>
                              </div>
                              <Button 
                                variant="link" 
                                size="sm" 
                                className="p-0 h-auto mt-2"
                                onClick={() => window.open(seller.representationAgreement.documentUrl, '_blank')}
                              >
                                {t.viewAgreement}
                              </Button>
                            </div>
                          )}

                          {/* AJOUTÉ — deals réellement soumis (payés/finalisés) pour ce
                              vendeur, avec leur nom — jusqu'ici seul un agrégat sans nom
                              ("1 Actives" / montant total) apparaissait plus haut sur cette
                              carte, sans jamais identifier DE QUEL deal il s'agissait. */}
                          {(() => {
                            const sellerDeals = myListings.filter(d => (d as any).sellerId === seller.id);
                            if (sellerDeals.length === 0) return null;
                            return (
                              <div className="space-y-2">
                                <span className="text-xs font-medium text-muted-foreground">
                                  {language === 'fr' ? 'Deals soumis' : 'Submitted deals'}
                                </span>
                                {sellerDeals.map(deal => (
                                  <div key={deal.id} className="flex items-center justify-between gap-2 rounded-md border p-2">
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium truncate">{deal.businessName}</p>
                                      <p className="text-xs text-muted-foreground">{formatCurrency(deal.askingPrice, deal.currency)}</p>
                                    </div>
                                    <div className="flex gap-1.5 shrink-0">
                                      {/* AJOUTÉ — copie PDF du formulaire brut, pour dossier
                                          client ou référence ultérieure. */}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDownloadDealFormPdf(deal.id)}
                                        disabled={downloadingDealId === deal.id}
                                        title={language === 'fr' ? 'Télécharger le formulaire (PDF)' : 'Download form (PDF)'}
                                      >
                                        {downloadingDealId === deal.id ? (
                                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                          <FileText className="h-3.5 w-3.5" />
                                        )}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => navigate(`/broker/edit-deal/${deal.id}`)}
                                      >
                                        {language === 'fr' ? 'Voir' : 'View'}
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}

                          {/* Actions principales existantes */}
                          <div className="flex gap-2 flex-wrap">
                            <Button variant="outline" size="sm" onClick={() => navigate(`/broker/seller/${seller.id}`)}>
                              {t.viewFullProfile}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => window.open(`/calendar/merged/${seller.calendarId}`, '_blank')}>
                              <Calendar className="mr-2 h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleStartConversation(seller)}>
                              <MessageSquare className="mr-2 h-4 w-4" />
                              {t.message}
                            </Button>
                          </div>

                          {/* AJOUTÉ — Envoyer/Renvoyer l'invitation + Copier le lien,
                              visibles tant que le portail n'est pas actif */}
                          {!seller.hasPortalAccess && (
                            <div className="flex gap-2 flex-wrap pt-1 border-t mt-1">
                              <Button
                                size="sm"
                                onClick={() => handleResendInvite(seller)}
                                disabled={invitingSellerId === seller.id}
                                className="gap-1.5"
                              >
                                {invitingSellerId === seller.id ? (
                                  <><Loader2 className="h-4 w-4 animate-spin" />{t.sendingInvite}</>
                                ) : (
                                  <><Mail className="h-4 w-4" />{seller.inviteStatus ? t.resendInvite : t.sendInvite}</>
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCopyInviteLink(seller)}
                                className="gap-1.5"
                              >
                                <Copy className="h-4 w-4" />
                                {t.copyLink}
                              </Button>
                            </div>
                          )}

                          {/* AJOUTÉ — liste des formulaires/deals en cours pour ce vendeur,
                              une entrée par compagnie (remplace les anciennes sections uniques
                              "Mon brouillon"/"Formulaire de deal", qui ne pouvaient représenter
                              qu'UN SEUL cas à la fois — voir representedSellers/{id}/dealForms).
                              Les entrées déjà finalisées (deal réellement créé) ne sont plus
                              affichées ici. */}
                          {(dealFormsBySeller[seller.id] || [])
                            .filter(e => !e.finalizedDealId)
                            // AJOUTÉ — une entrée 'shared' déjà reprise par le broker
                            // (openedAsFormId présent) est désormais entièrement
                            // représentée par sa copie plus bas dans la liste —
                            // l'afficher aussi ici créait la confusion à deux entrées
                            // pour la même compagnie que tu as remarquée.
                            .filter(e => !(e.origin === 'shared' && e.openedAsFormId))
                            .length > 0 && (
                            <div className="pt-1 border-t mt-1 space-y-2">
                              <span className="text-xs font-medium text-muted-foreground">
                                {language === 'fr' ? 'Formulaires en cours' : 'Forms in progress'}
                              </span>
                              {(dealFormsBySeller[seller.id] || [])
                                .filter(e => !e.finalizedDealId)
                                .filter(e => !(e.origin === 'shared' && e.openedAsFormId))
                                .map(entry => (
                                <div key={entry.id} className="rounded-md border bg-muted/30 p-3 space-y-1.5">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-sm font-medium truncate">
                                      {entry.liveBusinessName || (language === 'fr' ? 'Sans nom (pas encore rempli)' : 'Unnamed (not yet filled)')}
                                    </span>
                                    {/* CORRIGÉ — une copie créée à partir d'un formulaire
                                        DÉJÀ approuvé par le vendeur (copiedFromFormId) n'est
                                        plus un simple "brouillon" au sens où le broker
                                        partirait de zéro — elle reste "approuvée par le
                                        vendeur", le broker ne fait que la finaliser. Badge
                                        distinct pour éviter la confusion signalée. */}
                                    {entry.origin === 'broker' ? (
                                      entry.copiedFromFormId ? (
                                        <Badge className="gap-1 shrink-0 text-xs" style={{ background: "rgba(206,171,101,0.15)", color: "#8a6d1f", border: "1px solid rgba(206,171,101,0.4)" }}>
                                          <CheckCircle2 className="h-3 w-3" />
                                          {language === 'fr' ? 'Approuvé par le vendeur — en finalisation' : 'Approved by seller — being finalized'}
                                        </Badge>
                                      ) : (
                                        <Badge variant="outline" className="gap-1 shrink-0 text-xs">
                                          <FilePen className="h-3 w-3" />
                                          {t.draftInProgress}
                                        </Badge>
                                      )
                                    ) : entry.liveStatus === 'submitted' ? (
                                      <Badge className="gap-1 shrink-0 text-xs" style={{ background: "rgba(206,171,101,0.15)", color: "#8a6d1f", border: "1px solid rgba(206,171,101,0.4)" }}>
                                        <CheckCircle2 className="h-3 w-3" />
                                        {t.formSubmittedBadge}
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline" className="shrink-0 text-xs">
                                        {entry.liveStatus === 'in_progress' ? t.formStatusInProgress : t.formStatusPending}
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex gap-2 flex-wrap items-center">
                                    {entry.origin === 'broker' ? (
                                      <Button
                                        size="sm"
                                        onClick={() => handleResumeDraft(seller, entry)}
                                        className="gap-1.5"
                                        style={{ background: '#CEAB65', color: '#1D314D' }}
                                      >
                                        <FilePen className="h-3.5 w-3.5" />
                                        {entry.copiedFromFormId
                                          ? (language === 'fr' ? 'Compléter le formulaire' : 'Complete the form')
                                          : t.resumeDraft}
                                      </Button>
                                    ) : entry.liveStatus === 'submitted' ? (
                                      <Button
                                        size="sm"
                                        onClick={() => handleOpenSellerForm(seller, entry)}
                                        className="gap-1.5"
                                        style={{ background: '#CEAB65', color: '#1D314D' }}
                                      >
                                        <FileText className="h-3.5 w-3.5" />
                                        {t.openSellerForm}
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleShareForm(seller, entry)}
                                        disabled={sharingFormSellerId === seller.id}
                                        className="gap-1.5"
                                      >
                                        {sharingFormSellerId === seller.id ? (
                                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                          <ClipboardList className="h-3.5 w-3.5" />
                                        )}
                                        {t.reshareForm}
                                      </Button>
                                    )}
                                    {entry.updatedAt?.toDate && (
                                      <span className="text-[11px] text-muted-foreground">
                                        {t.lastSaved} {entry.updatedAt.toDate().toLocaleDateString()}
                                      </span>
                                    )}
                                    {/* AJOUTÉ — retirer cette entrée (nettoyage manuel d'un
                                        duplicata ou d'un brouillon abandonné). */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDiscardEntry(seller, entry)}
                                      className="gap-1 text-muted-foreground hover:text-destructive ml-auto"
                                      title={language === 'fr' ? 'Retirer ce formulaire' : 'Discard this form'}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* AJOUTÉ — partager un NOUVEAU formulaire vierge (toujours une
                              nouvelle entrée, distincte des précédentes — un même vendeur
                              peut avoir plusieurs compagnies en cours). */}
                          <div className="pt-1 border-t mt-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleShareForm(seller)}
                              disabled={sharingFormSellerId === seller.id}
                              className="gap-1.5"
                            >
                              {sharingFormSellerId === seller.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <ClipboardList className="h-3.5 w-3.5" />
                              )}
                              {t.shareForm}
                            </Button>
                            <p className="text-[11px] text-muted-foreground mt-1">{t.shareFormHint}</p>
                          </div>

                          {/* AJOUTÉ — Modifier / Supprimer */}
                          <div className="flex gap-2 flex-wrap pt-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingSeller(seller)}
                              className="gap-1.5 text-muted-foreground"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              {t.editSeller}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRequestDeleteSeller(seller)}
                              className="gap-1.5 text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              {t.deleteSeller}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                {t.messages}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Conversations List */}
                <div className="border rounded-lg">
                  <div className="p-3 border-b font-medium">Conversations</div>
                  <ScrollArea className="h-96">
                    {representedSellers.length > 0 ? (
                      representedSellers.map(seller => {
                        const conv = conversations.find(c => c.participantId === seller.id);
                        return (
                          <div 
                            key={seller.id} 
                            className={`p-3 border-b hover:bg-muted/50 cursor-pointer ${activeConversation === conv?.id ? 'bg-muted' : ''}`}
                            onClick={() => handleStartConversation(seller)}
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback>{seller.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{seller.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{conv?.lastMessage || seller.company}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-6 text-center text-muted-foreground text-sm">
                        {t.noMessages}
                      </div>
                    )}
                  </ScrollArea>
                </div>

                {/* Message Thread */}
                <div className="md:col-span-2 border rounded-lg flex flex-col">
                  <div className="p-3 border-b font-medium">
                    {activeConversation 
                      ? conversations.find(c => c.id === activeConversation)?.participantName 
                      : t.startConversation}
                  </div>
                  <ScrollArea className="flex-1 h-72 p-4">
                    {messages.length > 0 ? (
                      <div className="space-y-3">
                        {messages.map(msg => (
                          <div key={msg.id} className={`flex ${msg.senderId === user?.id ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[70%] p-3 rounded-lg ${msg.senderId === user?.id ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                              <p className="text-sm">{msg.content}</p>
                              <p className="text-xs opacity-70 mt-1">
                                {msg.sentAt?.toDate?.()?.toLocaleTimeString() || ''}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-muted-foreground text-sm py-12">
                        {t.noMessages}
                      </div>
                    )}
                  </ScrollArea>
                  <div className="p-3 border-t">
                    <div className="flex gap-2">
                      <Textarea 
                        placeholder={t.typeMessage}
                        className="flex-1 resize-none"
                        rows={2}
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSendMessage())}
                      />
                      <Button className="self-end" onClick={handleSendMessage} disabled={sending || !messageText.trim()}>
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : t.send}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5" />
                {t.documents}
              </CardTitle>
              <div>
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
                <Button onClick={() => { setUploadType('contract'); fileInputRef.current?.click(); }}>
                  <Upload className="mr-2 h-4 w-4" />
                  {t.uploadDocument}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="contracts" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="contracts" onClick={() => setUploadType('contract')}>{t.contracts}</TabsTrigger>
                  <TabsTrigger value="agreements" onClick={() => setUploadType('agreement')}>{t.agreements}</TabsTrigger>
                  <TabsTrigger value="financials" onClick={() => setUploadType('financial')}>{t.financials}</TabsTrigger>
                  <TabsTrigger value="other" onClick={() => setUploadType('other')}>{t.other}</TabsTrigger>
                </TabsList>

                {(['contract', 'agreement', 'financial', 'other'] as const).map(docType => (
                  <TabsContent key={docType} value={docType === 'contract' ? 'contracts' : docType === 'agreement' ? 'agreements' : docType === 'financial' ? 'financials' : 'other'}>
                    {docsLoading ? (
                      <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>
                    ) : getDocumentsByType(docType).length === 0 ? (
                      <div className="text-center py-12">
                        <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">{t.noDocuments}</p>
                        <Button variant="outline" className="mt-4" onClick={() => { setUploadType(docType); fileInputRef.current?.click(); }}>
                          <Upload className="mr-2 h-4 w-4" />{t.uploadDocument}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {getDocumentsByType(docType).map(doc => (
                          <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex items-center gap-3">
                              <FileText className="h-8 w-8 text-primary" />
                              <div>
                                <p className="font-medium">{doc.name}</p>
                                <p className="text-xs text-muted-foreground">{formatFileSize(doc.size)}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline"><Download className="h-4 w-4" /></Button>
                              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteDocument(doc.id)}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Consultants Tab - External consultants with rates, availability */}
        <TabsContent value="consultants" className="space-y-4">
          <ConsultantsTab
            consultants={[
              {
                id: "c1",
                name: "Me Sophie Dubois",
                specialty: "Business Law",
                bio: "15+ ans d'expérience M&A pour PME. Approche pragmatique.",
                email: "sophie.dubois@expert.ca",
                phone: "+1 514 555 0101",
                status: "available",
                hourlyRate: 250,
                currency: "CAD",
                location: "Montreal, QC"
              },
              {
                id: "c2",
                name: "Me Karim Benali",
                specialty: "Immigration",
                bio: "Spécialiste immigration d'affaires, visa investisseur.",
                email: "karim.benali@expert.ca",
                phone: "+1 514 555 0102",
                status: "available",
                hourlyRate: 200,
                currency: "CAD",
                location: "Montreal, QC"
              },
              {
                id: "c3",
                name: "Julie Tremblay, CPA",
                specialty: "Tax",
                bio: "Conseillère fiscale PME, structuration d'acquisition.",
                email: "julie.tremblay@expert.ca",
                phone: "+1 514 555 0103",
                status: "engaged",
                hourlyRate: 180,
                currency: "CAD",
                location: "Quebec City, QC"
              }
            ]}
            favoriteConsultants={favoriteConsultants}
            language={language}
            onToggleFavorite={(id) => {
              const consultant = [
                { id: "c1", name: "Me Sophie Dubois" },
                { id: "c2", name: "Me Karim Benali" },
                { id: "c3", name: "Julie Tremblay, CPA" }
              ].find(c => c.id === id);
              toggleConsultantFavorite(id, consultant?.name);
            }}
            onContact={(id) => toast({ title: language === 'fr' ? "Demande envoyée" : "Request sent", description: `Consultant: ${id}` })}
            gateContactTier="essential"
          />
        </TabsContent>

        {/* Partners Tab */}
        <TabsContent value="partners" className="space-y-4">
          <PartnersTab
            partners={[]}
            favoriteConsultants={favoriteConsultants}
            language={language}
            onToggleFavorite={toggleConsultantFavorite}
            onContact={(id) => toast({ title: language === 'fr' ? "Contact demandé" : "Contact requested", description: `Partner: ${id}` })}
          />
        </TabsContent>

        {/* Experts Tab - Internal AcquizGroup experts */}
        <TabsContent value="experts" className="space-y-4">
          <ExpertsTab
            experts={[]}
            language={language}
            userRegion={userRegion}
            onBookConsultation={(id) => toast({ title: language === 'fr' ? "Réservation initiée" : "Booking initiated", description: `Expert: ${id}` })}
            onMessage={(id) => toast({ title: language === 'fr' ? "Message envoyé" : "Message sent", description: `Expert: ${id}` })}
          />
        </TabsContent>


        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-4">
          <CalendarTab showAvailabilitySettings={true} />
        </TabsContent>

        {/* List New Business Tab */}
        {/* CORRIGÉ — brokerMode + resumeSellerId : le formulaire démarre
            toujours vierge sauf reprise explicite d'un brouillon précis
            (voir handleResumeDraft/handleNewListing ci-dessus). onComplete
            réinitialise resumeSellerId, pour que le prochain "Nouvelle
            Annonce" reparte bien à zéro même après une reprise. */}
        <TabsContent value="list-business" className="space-y-4">
          <SubmitDealForm
            brokerMode
            resumeSellerId={resumeSellerId || undefined}
            resumeFormId={resumeFormId || undefined}
            onComplete={() => { setResumeSellerId(null); setResumeFormId(null); refreshDealForms(); setActiveTab("overview"); }}
          />
        </TabsContent>
      </Tabs>

            {/* Add Seller Modal */}
            <Dialog open={showAddSeller} onOpenChange={setShowAddSeller}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t.addRepresentedSeller}</DialogTitle>
                  <DialogDescription>{t.addSellerDescription}</DialogDescription>
                </DialogHeader>
                <AddSellerForm 
                  onClose={() => setShowAddSeller(false)} 
                  onAdd={addRepresentedSeller}
                  t={t}
                />
              </DialogContent>
            </Dialog>

            {/* AJOUTÉ — Edit Seller Modal */}
            <Dialog open={!!editingSeller} onOpenChange={(open) => !open && setEditingSeller(null)}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t.editSellerTitle}</DialogTitle>
                  <DialogDescription>{t.editSellerDesc}</DialogDescription>
                </DialogHeader>
                {editingSeller && (
                  <EditSellerForm
                    seller={editingSeller}
                    onClose={() => setEditingSeller(null)}
                    onSave={handleSaveEditSeller}
                    t={t}
                  />
                )}
              </DialogContent>
            </Dialog>

            {/* AJOUTÉ — Delete Seller Confirmation */}
            <AlertDialog open={!!deletingSeller} onOpenChange={(open) => !open && setDeletingSeller(null)}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t.deleteSellerTitle}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {checkingDeleteEligibility ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {language === 'fr' ? 'Vérification...' : 'Checking...'}
                      </span>
                    ) : deletingSellerDealCount > 0 ? (
                      t.deleteSellerDescBlocked.replace('{count}', String(deletingSellerDealCount))
                    ) : (
                      t.deleteSellerDescConfirm.replace('{name}', deletingSeller?.name || '')
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t.cancel}</AlertDialogCancel>
                  {!checkingDeleteEligibility && deletingSellerDealCount === 0 && (
                    <AlertDialogAction
                      onClick={handleConfirmDeleteSeller}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {t.deleteSellerConfirm}
                    </AlertDialogAction>
                  )}
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* AJOUTÉ — Dialogue du lien de formulaire partagé, avec champ
                sélectionnable et bouton de copie manuel. Remplace le
                toast qui disparaît seul et dont la copie automatique
                peut échouer silencieusement selon le contexte du
                navigateur (ex. IP locale en HTTP). */}
            <Dialog open={!!shareFormDialogUrl} onOpenChange={(open) => !open && setShareFormDialogUrl(null)}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t.dealForm}</DialogTitle>
                  <DialogDescription>{t.formSharedDesc}</DialogDescription>
                </DialogHeader>
                <div className="flex gap-2 items-center">
                  <Input
                    id="share-form-url-input"
                    readOnly
                    value={shareFormDialogUrl || ''}
                    onFocus={(e) => e.target.select()}
                    className="font-mono text-xs"
                  />
                  <Button onClick={handleCopyFromDialog} className="shrink-0 gap-1.5">
                    {shareFormDialogCopied ? (
                      <><CheckCircle2 className="h-4 w-4" />{language === 'fr' ? 'Copié !' : 'Copied!'}</>
                    ) : (
                      <><Copy className="h-4 w-4" />{language === 'fr' ? 'Copier' : 'Copy'}</>
                    )}
                  </Button>
                </div>
                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => setShareFormDialogUrl(null)}>
                    {t.cancel}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </main>
      </div>
    </div>
  );
}

// Add Seller Form Component
function AddSellerForm({ 
  onClose, 
  onAdd,
  t 
}: { 
  onClose: () => void; 
  onAdd: (data: Partial<RepresentedSeller>) => void;
  t: typeof translations.en;
}) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">{t.fullName} *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="company">{t.company} *</Label>
        <Input
          id="company"
          value={formData.company}
          onChange={(e) => setFormData({...formData, company: e.target.value})}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">{t.email} *</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone">{t.phone} *</Label>
        <Input
          id="phone"
          value={formData.phone}
          onChange={(e) => setFormData({...formData, phone: e.target.value})}
          required
        />
      </div>

      <div className="bg-muted/50 rounded-lg p-4">
        <p className="text-sm font-medium mb-2">{t.whatHappensNext}</p>
        <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
          <li>{t.step1}</li>
          <li>{t.step2}</li>
          <li>{t.step3}</li>
          <li>{t.step4}</li>
        </ul>
      </div>

      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onClose}>
          {t.cancel}
        </Button>
        <Button type="submit">
          <UserPlus className="mr-2 h-4 w-4" />
          {t.addSeller}
        </Button>
      </div>
    </form>
  );
}

// AJOUTÉ — Edit Seller Form Component (structure identique à AddSellerForm,
// pré-rempli, sans le bloc "what happens next" qui ne s'applique plus)
function EditSellerForm({
  seller,
  onClose,
  onSave,
  t
}: {
  seller: { name: string; email: string; phone: string; company: string };
  onClose: () => void;
  onSave: (data: { name: string; email: string; phone: string; company: string }) => void;
  t: typeof translations.en;
}) {
  const [formData, setFormData] = useState({
    name: seller.name || "",
    email: seller.email || "",
    phone: seller.phone || "",
    company: seller.company || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="edit-name">{t.fullName} *</Label>
        <Input
          id="edit-name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="edit-company">{t.company} *</Label>
        <Input
          id="edit-company"
          value={formData.company}
          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="edit-email">{t.email} *</Label>
        <Input
          id="edit-email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="edit-phone">{t.phone} *</Label>
        <Input
          id="edit-phone"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          required
        />
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onClose}>
          {t.cancel}
        </Button>
        <Button type="submit">
          {t.saveChanges}
        </Button>
      </div>
    </form>
  );
}